// SPV Pool v1.0 PR 5 — hand-written DTOs mirroring the Investor API
// contract (PR 3 backend). Source of truth:
//   src/PhoenixCapital.Core/DTOs/SpvPool/InvestorDtos.cs
//   src/PhoenixCapital.Core/Enums/SpvPool/InvestorEnums.cs
// Enums serialize as strings (JsonStringEnumConverter), so these are
// string-union types matching the C# member names.

export type InvestorType =
  | "IndividualAccredited"
  | "Institutional"
  | "FamilyOffice"
  | "Fund"
  | "QIB";

export const INVESTOR_TYPES: readonly InvestorType[] = [
  "IndividualAccredited",
  "Institutional",
  "FamilyOffice",
  "Fund",
  "QIB",
];

export type AccreditationBasis =
  | "IncomeTest"
  | "NetWorthTest"
  | "Professional"
  | "QIB"
  | "Other";

export const ACCREDITATION_BASES: readonly AccreditationBasis[] = [
  "IncomeTest",
  "NetWorthTest",
  "Professional",
  "QIB",
  "Other",
];

export type InvestorStatus = "Prospect" | "Active" | "Restricted" | "OffBoarded";

export const INVESTOR_STATUSES: readonly InvestorStatus[] = [
  "Prospect",
  "Active",
  "Restricted",
  "OffBoarded",
];

export type PreferredCommunicationMethod = "Email" | "Phone" | "MailingAddress";

export type InvestorCommitmentStatus =
  | "Indication"
  | "Committed"
  | "Funded"
  | "Withdrawn"
  | "Defaulted";

export const INVESTOR_COMMITMENT_STATUSES: readonly InvestorCommitmentStatus[] = [
  "Indication",
  "Committed",
  "Funded",
  "Withdrawn",
  "Defaulted",
];

/** Default Investor read shape — tax ID + banking values are masked. */
export interface InvestorMaskedDto {
  id: string;
  personId: string | null;
  entityId: string | null;
  investorType: InvestorType;
  accreditationBasis: AccreditationBasis;
  accreditationVerifiedAt: string | null;
  accreditationVerifiedById: string | null;
  amlKycVerifiedAt: string | null;
  taxIdMasked: string | null;
  bankingInstructionsMasked: string | null;
  hasTaxId: boolean;
  hasBankingInstructions: boolean;
  preferredCommunicationMethod: PreferredCommunicationMethod;
  status: InvestorStatus;
  notes: string | null;
  createdAt: string;
  modifiedAt: string | null;
}

/**
 * Plaintext Investor read shape. Returned ONLY by the gated
 * GET /api/investors/{id}/sensitive endpoint. Never persisted client-side.
 */
export interface InvestorSensitiveDto {
  id: string;
  taxId: string | null;
  bankingInstructions: string | null;
}

export interface InvestorCreate {
  personId?: string | null;
  entityId?: string | null;
  investorType: InvestorType;
  accreditationBasis: AccreditationBasis;
  accreditationVerifiedAt?: string | null;
  accreditationVerifiedById?: string | null;
  amlKycVerifiedAt?: string | null;
  taxId?: string | null;
  bankingInstructions?: string | null;
  preferredCommunicationMethod: PreferredCommunicationMethod;
  status: InvestorStatus;
  notes?: string | null;
}

export interface InvestorUpdate {
  investorType?: InvestorType;
  accreditationBasis?: AccreditationBasis;
  accreditationVerifiedAt?: string | null;
  accreditationVerifiedById?: string | null;
  amlKycVerifiedAt?: string | null;
  taxId?: string | null;
  bankingInstructions?: string | null;
  preferredCommunicationMethod?: PreferredCommunicationMethod;
  status?: InvestorStatus;
  notes?: string | null;
}

export interface InvestorListQuery {
  page?: number;
  pageSize?: number;
  q?: string;
  status?: InvestorStatus | string;
  investorType?: InvestorType | string;
  personId?: string;
  entityId?: string;
}

export interface InvestorCommitmentDto {
  id: string;
  investorId: string;
  spvPoolId: string;
  poolTrancheId: string;
  committedAmount: number;
  fundedAmount: number;
  commitmentDate: string;
  fundingDate: string | null;
  subscriptionAgreementDocumentId: string | null;
  status: InvestorCommitmentStatus;
  notes: string | null;
  createdAt: string;
  modifiedAt: string | null;
}

export interface InvestorCommitmentCreate {
  investorId: string;
  spvPoolId: string;
  poolTrancheId: string;
  committedAmount: number;
  fundedAmount: number;
  commitmentDate: string;
  fundingDate?: string | null;
  subscriptionAgreementDocumentId?: string | null;
  status: InvestorCommitmentStatus;
  notes?: string | null;
}

export interface InvestorCommitmentListQuery {
  page?: number;
  pageSize?: number;
  investorId?: string;
  spvPoolId?: string;
  poolTrancheId?: string;
  status?: InvestorCommitmentStatus | string;
}

export interface InvestorDistributionDto {
  id: string;
  investorCommitmentId: string;
  periodStart: string;
  periodEnd: string;
  grossAmount: number;
  withheldAmount: number;
  netAmount: number;
  paidAt: string | null;
  relatedWaterfallDistributionId: string | null;
  notes: string | null;
  createdAt: string;
  modifiedAt: string | null;
}

export interface InvestorDistributionListQuery {
  page?: number;
  pageSize?: number;
  investorCommitmentId?: string;
}
